import { useState } from 'react';
import { Button } from "@progress/kendo-react-buttons";
import PilotageApiRepository from '../../../repositories/api/PilotageApiRepository';
import Moment from 'moment';
import Spacer from '../../../components/layout/Spacer';
import { isNullOrEmpty, isObjectNull } from '../../../components/helpers/ObjectHelpers';
import { getPilotAssignmentTransportOrderBorderColor } from '../pilot-assignment/helpers/PilotAssignmentHelpers';
import { TimePicker } from '../../../components/layout/date-time/TimePicker';
import { MiniCard } from '../../../components/layout/card/MiniCard';
import { useSuccessNotification, useWarningNotification } from 'components/hooks/useAppStore';

const PilotageTransportOrder = ({
    state: pilotTransportOrder,
    pilotAssignmentId,
    onReload,
    index,
    pilotIsConfirmedAndOnline,
    estimatedStart,
    isReadOnly
}) => {

    const [showDateTimePicker, setShowDateTimePicker] = useState(false);
    const [transportDate, setTransportDate] = useState(estimatedStart);
    const [orderButtonActivated, setOrderButtonActivated] = useState(false);
    const [isBusy, setIsBusy] = useState(false);

    const timeType = pilotTransportOrder.pilotTransportOrderTimeType.value === "PICKUP_TIME" ? "Henting" : "Levering";

    const addSuccessNotification = useSuccessNotification();
    const addWarningNotification = useWarningNotification();

    return (
        <div>
            <div className="transport-order-container">
                <MiniCard
                    item={{
                        borderColor: getPilotAssignmentTransportOrderBorderColor(pilotTransportOrder.pilotTransportOrderStatus.systemName),
                        text: `${timeType} - ${pilotTransportOrder.pilotTransportOrderStatus.nameNor.toLowerCase()}`
                    }}
                />
                    <a href={`tel: ${pilotTransportOrder.contactNumber}`} className="transport-order-phone-link">{pilotTransportOrder.contactNumber}</a>
            </div>
            <Spacer height={15} />
            <div className="details-transportorder-location m-b-8">
                <div className="m-b-8">
                    {`Fra: ${pilotTransportOrder.pilotPickupLocation.name}`}
                </div>
                <div>
                    {`Til: ${pilotTransportOrder.pilotDeliverLocation.name}`}
                </div>
            </div>
            {
                (!isReadOnly) &&
                <>
                    {RenderOrderTime()}
                    {!editTransportTimeIsDisabled() && canPilotOrderPilotTransportOrder() &&
                        <div className="details-transportorder-warning-time m-b-5">
                            {`Varslingstid er ${pilotTransportOrder.minimumMinutesBeforeToSend} minutter ${transportMessageSendtTimeText()}`}
                        </div>
                    }
                </>
            }
        </div>
    )

    function RenderOrderTime() {
        if (pilotTransportOrder.pilotTransportOrderTimeType.value === "DELIVER_TIME" && pilotTransportOrder.pilotTransportOrderStatus.value === "ALLOCATED") {
            return (<></>);
        }
        if (pilotIsConfirmedAndOnline() && canPilotOrderPilotTransportOrder() && !editTransportTimeIsDisabled()) {
            return (
                <>
                    <div className="details-transportorder-order">
                        <TimePicker
                            adaptive={true}
                            title="Tidspunkt"
                            value={transportDate}
                            onChange={(e) => onDateChange(e)}
                            disabled={editTransportTimeIsDisabled() || isBusy}
                            canDelete={false}
                            showPrefix={true}
                        />
                        <Button
                            onClick={isOrdering() ? onOrderPilotTransportOrder : onUpdatePilotTransportOrder}
                            themeColor="primary"
                            disabled={(!orderButtonActivated && isBusy) || transportDate === null}>
                            {isOrdering() ? "Bestill" : "Oppdater"}
                        </Button>
                    </div>
                </>
            );
        } else {
            return (
                <div className="details-transportorder-time">
                    {isNullOrEmpty(transportDate) ? `${pilotTransportOrder.pilotTransportOrderTimeType.nameNor}: ...` : `${pilotTransportOrder.pilotTransportOrderTimeType.nameNor}: ${Moment(transportDate).format('HH:mm')}`}
                </div>
            )
        }
    }

    function isOrdering() {
        return pilotTransportOrder.pilotTransportOrderStatus.value !== "ORDERED";
    }

    // should determine if one can order a pilot transport order
    function canPilotOrderPilotTransportOrder() {
        return pilotTransportOrder.pilotTransportOrderType.value === "BY_PILOT"
            && pilotTransportOrder.pilotTransportOrderTimeType.value === "PICKUP_TIME"
            && (pilotTransportOrder.pilotTransportOrderStatus.value === "ALLOCATED" || pilotTransportOrder.pilotTransportOrderStatus.value === "ORDERED");
    }

    function editTransportTimeIsDisabled() {
        return pilotTransportOrder.transportOrderMessageSendt || !navigator.onLine;
    }

    async function onOrderPilotTransportOrder() {
        setIsBusy(true);
        const response = await PilotageApiRepository.orderPilotTransportOrder(pilotAssignmentId, pilotTransportOrder.pilotTransportOrderId, transportDate);
        if (response.ok === true) {
            addSuccessNotification(`Tilbringerordren ble bestilt`);
            setOrderButtonActivated(!orderButtonActivated);
            if (getTransportMessageSendtTime() <= new Date()) {
                pilotTransportOrder.transportOrderMessageSendt = true;
            }
            pilotTransportOrder.pilotTransportOrderStatus = {
                nameNor: 'Bestilt',
                systemName: 'ORDERED',
                value: 1
            };
            pilotTransportOrder.pilotTransportOrderTime = transportDate;
            onReload(index, pilotTransportOrder);
        } else {
            var message = await response.json();
            addWarningNotification(message);
        }
        setIsBusy(false);
    }

    async function onUpdatePilotTransportOrder() {
        setIsBusy(true);
        const response = await PilotageApiRepository.updatePilotTransportOrder(pilotAssignmentId, pilotTransportOrder.pilotTransportOrderId, transportDate);
        if (response.ok === true) {
            addSuccessNotification(`Tilbringerordren sitt tidspunkt ble oppdatert`);
            setOrderButtonActivated(!orderButtonActivated);
            if (getTransportMessageSendtTime() <= new Date()) {
                pilotTransportOrder.transportOrderMessageSendt = true;
            }
            pilotTransportOrder.pilotTransportOrderTime = transportDate;
            onReload(index, pilotTransportOrder);
        } else {
            var message = await response.json();
            addWarningNotification(message);
        }
        setIsBusy(false);
    }

    // Contains work-around for adaptive mode for timePicker. Accepted by component developers.
    function onDateChange(e) {
        let newDate = null;

        if (!isObjectNull(e)) {
            newDate = Moment(e);
        }

        setTransportDate(newDate);
        setShowDateTimePicker(!showDateTimePicker);
    }

    function transportMessageSendtTimeText() {
        const transportMessageSendtTime = Moment(getTransportMessageSendtTime());
        if (transportMessageSendtTime.isValid() && (getTransportMessageSendtTime() > new Date())) {
            return `(bestillingen sendes ${transportMessageSendtTime.format('HH:mm')})`
        } else {
            return "";
        }
    }

    function getTransportMessageSendtTime() {
        var messageSendtDate = Moment(transportDate)
            .subtract(pilotTransportOrder.minimumMinutesBeforeToSend, "minutes");
        return messageSendtDate;
    }
}

export default PilotageTransportOrder;
